import { KitOrder } from 'api/Responses/OrderResponses';
import { OnboardingStatus, TrayOnboardingStatus } from 'api/Responses/SatoResponses';
import { VideoType } from 'api/Responses/TrayResponses';

const en = {
  App: {
    savi_has_moved_text_start: 'We no longer support this application.',
    click_here_text: ' Click here',
    savi_has_moved_text_end: ' to be taken to the new SATO app in the appstore.',
  },
  HomePage: {
    title: 'Welcome to SADL - Set and Device Library',
    content:
      'SADL is a web based application enabling users to easily interact with the set and device data which is loaded into the SAVI and SATO mobile apps. Here, you can create, view and update sets and devices.',
  },
  EmptyState: {
    noResults: 'There are no items to display.',
  },
  Navigation: {
    createSet: 'Create Set',
    createDevice: 'Create Device',
    logout: 'Logout',
  },
  Routes: {
    drafts: 'Drafts',
    review: 'Review',
    approve: 'Approve',
    sato: 'Sato',
    userManagement: 'User Management',
    deliveryPortal: 'Delivery Portal',
    dataUpload: 'Data Upload',
  },
  AsyncImage: {
    fail: 'Could not load image',
    none: 'No image available',
  },
  DataUpload: {
    title: 'Data Upload',
    menu: {
      uploadList: 'Upload List',
      upload: 'Upload',
      templates: 'Templates',
    },
    fileUpload: {
      title: 'New Upload',
      text: 'Drag and drop your file/s here, or',
      browse: 'browse',
      note: 'Only supports csv files. Max 1 file.',
      type: 'Type',
      name: 'Name (size: MB)',
      delete: 'Delete',
      noUpload: 'No files uploaded',
      countUploadedFiles: ' uploaded file/s',
      error: {
        fileType: 'File type must be "csv"',
        upload: 'Unable to upload file. Check the format and try again',
        setsMissing: 'Some sets in this file do not exist in SAVI. Please create these sets and try again.',
      },
      success: {
        upload: 'Successfully uploaded',
      },
      scan: 'Scanning for duplicates',
      submit: 'Upload',
      cancel: 'Reset',
    },
    template: {
      title: 'Download Templates',
      bodyText1: `To upload data to SADL, you must follow the format of the templates provided otherwise your data won't be
          uploaded and you will receive an error notification.`,
      bodyText2: `There are 4 templates, 2 for device uploads and 2 for BOM uploads. Each type of upload has a data upload which
          contains only BOM or Device data, and the second upload is for images and attachments.`,
      deviceTemplatesTitle: 'Device Upload Templates:',
      deviceTemplatesFileName: 'DEVICE_UPLOAD_DATA_TEMPLATE.csv',
      bomTemplatesTitle: 'BOM Upload Templates:',
      bomTemplatesFileName: 'BOM_UPLOAD_DATA_TEMPLATE.csv',
      bomSerialNumberTemplatesTitle: 'BOM Serial Number Templates:',
      bomSerialNumberTemplatesFileName: 'SET_SERIAL_NUMBER_UPLOAD_TEMPLATE.csv',
      close: 'Close',
    },
    approveModal: {
      warning: 'Warning!',
      approveAllWarningText:
        'Selecting "Approve All" will approve all the data that is in the Update tab which already has existing data in SADL. So when you proceed with the upload, a new draft will be created overwriting the data that is already in SADL. If you would like to review each duplicate Set / BOM data one by one, select "No, Go Back" and then select "View Data" on the device row in the table.',
      goBack: 'No, Go back',
      approveAll: 'Approve all',
    },
    editDataUploadMerge: {
      filename: 'Filename: ',
      inspectInstruction: '*Please inspect the data that was uploaded below',
    },
    dataUploadMergeTable: {
      updateAll: 'Update All Data',
    },
    deviceConflictModal: {
      existingDataTitle: 'Existing Data in SADL:',
      existingDataSubtitle: 'This data is attached to the Set already in SADL:',
      field: 'Field',
      input: 'Input',
      deviceCode: 'Device Code',
      deviceDescription: 'Device Description',
      numParts: 'Number of parts',
      updatedData: 'New Updated Data:',
      updatedDataSubtitle: 'This data is what has been uploaded in the .csv:',
      duplicateDataUploadAlert:
        'Note: If you proceed with uploading the duplicate data, a new draft of the device will be created with data in it. From here the user can merge the new data in.',
      ignoreDuplicateData: 'Ignore Duplicate Data',
      approveDuplicateData: 'Approve Duplicate Data',
    },
    ignoreUploadModal: {
      ignoreUpload: 'Ignore Upload?',
      warning:
        'Selecting "Yes, Ignore" will move this set data to the ignore tab and it will not be uploaded into the database. You can add it back by going to the ignore tab.',
      cancel: 'Cancel',
      ignore: 'Yes, Ignore',
    },
    proceedWithCreateDraftsModal: {
      proceedWithUpload: 'Proceed with Upload?',
      yes: 'Selecting "Yes, proceed will upload all the data in the .csv to SADL, please wait as this may take some time depending on the size of the file."',
      goBack: 'No, Go back',
      proceed: 'Proceed',
      success: 'Drafts were successfully created',
      exit: 'Exit Upload',
    },
    warningUnprocessedDuplicatesModal: {
      title: 'Unprocessed Duplicates',
      subtitle:
        'Please either "ignore" or override all of the items in the update tab by clicking on "Investigate". Please do this before pressing proceed.',
      return: 'Continue',
    },
    table: {
      status: 'Status',
      fileName: 'Filename',
      uploadType: 'Upload Type',
      uploader: 'Uploader',
      dateUploaded: 'Date Uploaded',
      totalUploadedItems: 'Total Drafts Uploaded',
      nil: 'Nil',
      nilToolTip: 'This upload has not been processed so no drafts have been uploaded yet.',
      download: 'Download',
      setCode: 'Set Code',
      setName: 'Set Name',
      numberOfTrays: 'Number of Trays',
      numberOfDevices: 'Number of Devices',
      investigate: 'Investigate',
      newDraft: 'New Draft',
      ignoreUpload: 'IgnoreUpload',
      refresh: 'Refresh',
    },
    compareDuplicateUploadModal: {
      title: 'Compare Duplicate Upload: ',
    },
    restoreModal: {
      restoreUpload: 'Restore Upload?',
      restoreSubtitle:
        'Selecting "Yes, Restore" will move this set data back to the unique/update tab and it will be uploaded into the database. You can ignore it again by going to the unique or update tab.',
      cancel: 'Cancel',
      restore: 'Yes, Restore',
    },
    updateDeviceListItem: {
      ignoreUpload: 'Ignore Upload',
      restore: 'Restore',
      na: 'N/A',
      updateDeviceRow: 'Update Device Row',
      updateSetRow: 'Update Set Row',
      updateSetSerialRow: 'Update Set Serial Row',
      tooltip:
        'You can only restore items that have been manually ignored by the user. The data for this item is not able to be merged.',
    },
  },
  DraftSet: {
    sets: 'Sets',
    devices: 'Devices',
  },
  DraftActions: {
    getPictureList: 'Download Picture List',
    merge: 'Merge Draft',
    approve: 'Approve Draft',
    submit: 'Submit Draft',
    reject: 'Reject Draft',
    save: 'Save Draft',
    add: 'Add Tray/Device',
    delete: 'Delete Draft',
    create: 'Create Draft',
    confirmDelete: 'Are you sure you want to delete this draft',
    draftError: 'Could not fetch drafts',
    pictureListError: 'Error downloading Picture List',
    manageSerialNumbers: 'Manage Serial Numbers',
  },
  SettingsModal: {
    language: 'Language',
  },
  SetStatus: {
    UNTOUCHED: 'UNTOUCHED',
    MODIFIED: 'MODIFIED',
    DRAFT: 'DRAFT',
    REVIEW: 'REVIEW',
    PENDING: 'PENDING',
    MERGED: 'MERGED',
    VALIDATED: 'VALIDATED',
    TODO: 'TODO',
    REJECTED: 'REJECTED',
    ARCHIVED: 'ARCHIVED',
    APPROVED: 'APPROVED',
  },
  DeviceStatus: {
    'part.draft': 'DRAFT',
    'part.pending': 'PENDING',
  },
  DeviceTests: {
    debrisInspection: 'Debris Inspection',
    functionTest: 'Function Test',
    torqueTest: 'Torque Test',
    proneToDebris: 'Prone to Debris',
  },
  SetTable: {
    title: 'Sets',
    code: 'Set Code',
    revision: 'Revision',
    inspectionManual: 'Set Inspection Manual',
    inspectionManualUpload: 'Drag and drop file',
    inspectionManualNoFile: 'No file uploaded...',
    inspectionManualError: 'Get Inspection Manual error: Cannot find ',
    region: 'Region',
    jme: 'Group',
    status: 'Status',
    user: 'User',
    draft: 'DRAFT',
    name: 'Set Name',
  },
  TrayTable: {
    reference: 'Reference',
    name: 'Name',
    quantity: 'Quantity',
    nonStandard: 'Non-Standard',
    generalInspection: 'General Inspection',
    upload: 'Upload New Tray Image',
    notes: 'Tray Inspection Requirements',
    addDevices: 'Add devices',
  },
  DeviceTable: {
    title: 'Devices',
    reference: 'Ref',
    code: 'Code',
    description: 'Description',
    image: 'Image',
    quantity: 'Qty',
    actions: 'Actions',
    notes: 'Additional Inspection Requirements',
    selectAll: 'Select all devices in this tray',
    selectRow: 'Select device',
    selected: 'selected',
    deleteSelected: 'Delete selected',
    moveTo: 'Move to tray...',
  },
  DevicePage: {
    status: 'Status',
    cancelDuplicateUpload: 'Cancel',
    code: 'Device Code',
    numParts: 'Number of Parts',
    description: 'Description',
    imageAlreadyExists: 'A file with the same name already exists.',
    user: 'User',
    additionalInspection: 'Additional Inspection Requirements',
    japaneseDescription: 'Japanese Description',
    japaneseAdditionalInspection: 'Japanese Additional Inspection Requirements',
    quantity: 'Qty',
    cannulated: 'Cannulated',
    image: 'Part Image',
    upload: 'Upload New Device Image',
    uploadImageSuccess: 'Image uploaded',
    uploadDocSuccess: 'Document uploaded',
    existingImage: 'Select from existing images',
    existingDocument: 'Search device for documents',
    cannulatedYes: 'Yes',
    cannulatedNo: 'No',
    replaceImage: 'Replace',
  },
  CreateModal: {
    add: 'Add',
    setCodeTitle: 'Set Code',
    setCodePlaceholder: 'Enter new set code...',
    deviceCodeTitle: 'Device Code',
    deviceCodePlaceholder: 'Enter new device code...',
  },
  ConfirmModal: {
    confirm: 'Confirm',
    mergeAll: 'Are you sure you want to merge all drafts?',
    merge: 'Merge',
    cancelText: 'No, go back',
    delete: 'Delete',
    tray: 'Are you sure you want to delete this tray?',
    device: 'Are you sure you want to delete this device?',
    devices: 'Are you sure you want to delete the selected devices?',
    duplicateIn: 'Already in tray',
    duplicateMerge: 'Merge adds the quantities together. Cancel changes nothing.',
    cancel: 'Cancel',
  },
  EditDraftModal: {
    setTitle: 'Are you sure you want to create a draft of this set?',
    setMessage:
      'Only one draft is allowed per set, so creating a draft will lock this set from having another draft created',
    deviceTitle: 'Are you sure you want to create a draft of this device?',
    deviceMessage:
      'Only one draft is allowed per device, so creating a draft will lock this device from having another draft created',
    decline: 'No, Go Back',
    confirm: 'Yes, Create Draft',
  },
  BreadCrumbs: {
    draft: 'Draft',
    review: 'In Review',
    approve: 'Pending Approval',
    merged: 'Merged',
  },
  ReferenceImageModal: {
    title: 'Reference Image Generator',
    addTag: 'Add New Tag',
    removeTag: 'Remove Last Tag',
    download: 'Download',
    saveAndSubmit: 'Save & Submit',
    exit: 'Exit',
    imageLoadError: 'Error loading image',
    imageDownloadError: 'Failed to download image',
    imageSaveError: 'Failed to save image',
  },
  AddModal: {
    Item: {
      tray: 'Add Tray',
      device: 'Add Device',
    },
    Tray: {
      add: 'Add Tray',
      upload: 'Upload New Tray Image',
      uploadSuccess: 'Image uploaded',
      annotateSuccess: 'Image updated',
      uploadFailed: 'Could not upload tray image',
      referencePlaceholder: 'Enter the tray reference number...',
      namePlaceholder: 'Enter the tray name...',
      referenceWarning: 'Please add a tray reference',
      noImageError: 'A tray image is required',
      noNameError: 'A tray name is required',
    },
    Device: {
      add: 'Add Device',
      search: 'Search for a device...',
      fail: 'Failed to add new device',
      selectResult: 'Select device',
      addSelected: 'Add',
      device: 'device',
      devices: 'devices',
      clearSelection: 'Clear',
      selectedLabel: 'Selected:',
      headers: {
        tray: 'Select Tray',
        search: 'Select Device',
        add: 'Confirm Fields',
      },
    },
  },
  UserManagement: {
    name: 'Name',
    email: 'Email',
    role: 'Role',
    location: 'Location',
    active: 'Active',
    organisation: 'Organisation',
    delete: 'Delete',
    submitChanges: 'Submit Changes',
    none: 'None',
    success: 'Users updated.',
    error: 'User management change error: ',
    searchPlaceholder: 'Enter email',
    DeleteModal: {
      title: 'Delete User',
      placeholder: `Type the user's email`,
      submit: 'Delete',
      body: `Are you sure you want to delete {{email}}? Type their email below to confirm.`,
      errorMessage: 'Could not delete user: ',
      successMessage: 'Successfully deleted user',
    },
    createUser: 'Create User',
    CreateModal: {
      title: 'Create User',
      email: 'Email*',
      emailPlaceholder: `Type the user's email`,
      role: 'Role*',
      organisation: 'Organisation',
      location: 'Location*',
      submit: 'Create',
      body: `Please fill out the new user account's details below`,
      errorMessage: 'Could not create user: ',
      successMessage: 'Successfully created user',
    },
  },
  Sato: {
    status: {
      [OnboardingStatus.TODO]: 'TODO',
      [OnboardingStatus.DRAFT]: 'DRAFT',
      [OnboardingStatus.PENDING]: 'PENDING',
      [OnboardingStatus.REJECTED]: 'REJECTED',
      [OnboardingStatus.APPROVED]: 'APPROVED',
      [OnboardingStatus.ARCHIVED]: 'ARCHIVED',
      [TrayOnboardingStatus.MODEL_CONFIG]: 'MODEL CONFIG',
      [TrayOnboardingStatus.SKIPPED]: 'SKIPPED',
    },
    updateStatus: {
      approveSet: 'Approve Set',
      rejectSet: 'Reject Set',
      updateSet: 'Update Set Data',
      rejectSetConfirm: 'Are you sure you wish to reject this set?',
      rejectTray: 'Reject Tray',
      rejectTrayConfirm: 'Are you sure you wish to reject this tray?',
      setApproved: 'Set approved',
      setRejected: 'Set rejected',
      setUpdated: 'Set data updated',
    },
    setsPending: {
      all: 'All',
      pending: 'Pending',
      draft: 'Draft',
      filter: 'Filter Status',
      sort: 'Sort By',
      searchPlaceholder: 'Search for a submitted set in SATO',
      code: 'Set Code',
      status: 'Status',
      dateSubmitted: 'Date Submitted',
      dateApproved: 'Approval Date',
      resubmitted: 'Resubmitted',
      author: 'Submitted By',
      region: 'Region',
      jme: 'Group',
      yes: 'Yes',
      no: 'No',
      name: 'Set Name',
    },
    videosPage: {
      category: 'Category/Video Link',
      status: 'Status',
      comments: 'Comments',
      addComment: 'Add comment (required if rejecting)',
      approve: 'Approve Tray',
      reject: 'Reject Tray',
      skip: 'Skip Tray',
      approveVideo: 'Approve Video',
      rejectVideo: 'Reject Video',

      /**
       * ARG0 - Video type enum, e.g. 'Fully complete tray'
       * ARG1 - Video order
       */
      reviewVideoModalTitle: 'Review Tray - $ARG0 #$ARG1',
      trayReviewCompleted: {
        [TrayOnboardingStatus.APPROVED]: 'Tray approved',
        [TrayOnboardingStatus.REJECTED]: 'Tray rejected',
        [TrayOnboardingStatus.MODEL_CONFIG]: 'Tray submitted for model config',
        [TrayOnboardingStatus.SKIPPED]: 'Tray skipped',
      },
      trayRejectWithoutComment: 'Attempted to reject Tray without a comment!',
      cropTimes: 'Crop Times',
      videoError: 'Error displaying video. Please try using Safari.',
    },
    videoType: {
      [VideoType.ADDITIONAL]: 'Additional',
      [VideoType.IN_TRAY_COMPLETE]: 'Fully complete tray',
      [VideoType.IN_TRAY_INCOMPLETE]: 'Some parts removed',
      [VideoType.IN_TRAY_SHUFFLED]: 'Parts shuffled around tray',
      [VideoType.OUTISDE_TRAY]: 'Parts outside of tray',
      [VideoType.EMPTY_TRAY]: 'Empty tray',
    },
  },
  DraftCollection: {
    filterOptions: {
      set: 'Sets only',
      device: 'Devices only',
    },
    sortOptions: {
      newest: 'Newest',
      oldest: 'Oldest',
    },
    mergingItems: 'Merging items...',
    mergeAll: 'Merge All',
    showing: 'Showing {{itemsOnPage}} of {{activeItems}} results',
    noItems: 'No {{draftFilter}}s available.',
  },
  ManageSerialNumbers: {
    edit: 'Edit',
    history: 'History',
    back: 'Back',
    addSerialNumber: 'Add Serial Number',
    serialNumber: 'Serial Number',
    location: 'Location',
    active: 'Active',
    action: 'Action',
    noResults: 'No serial numbers attached...',
    Modal: {
      editTitle: 'Edit Serial Number',
      title: 'Add Serial Number',
      serialNumber: 'Serial Number',
      location: 'Location: ',
      enterUniqueSerialNumber: 'Enter Unique Serial Number: ',
      editUniqueSerialNumber: 'Edit Unique Serial Number: ',
      locaion: 'Location',
      submit: 'Proceed',
      cancel: 'Go Back',
      error: 'Could not add serial number',
      success: 'Serial number added',
      proceedStartText: 'Select "Proceed" to migrate this serial number to the set, ',
      proceedEndText: '. Or to go back, select "Go Back".',
      proceedActionWarning: 'Selecting "Proceed" will now attach this serial to the set data.',
      updated: 'Serial number updated',
    },
  },
  SerialNumberHistory: {
    title: 'Serial Number History',
    back: 'Back',
    noEvents: 'No history recorded in this date range',
    loadError: 'Could not load serial number history',
    fileUnavailable: 'File is not available',
    failed: 'Failed inspection',
    qualityRecord: 'Quality Record',
    customerChecklist: 'Customer Checklist',
    Labels: {
      by: 'By',
      orderNumber: 'Order #',
      deliveryNumber: 'Delivery #',
      surgeryDate: 'Surgery',
      surgeon: 'Surgeon',
      devices: 'Miscounted / failed devices',
    },
    Filters: {
      from: 'From',
      to: 'To',
    },
    events: {
      SERIAL_CREATED: 'Serial created',
      ORDER_UPLOAD: 'Attached to order',
      INSPECTION: 'Inspection',
    },
    workflows: {
      INBOUND: 'Inbound',
      OUTBOUND: 'Outbound',
      INSPECTION: 'Inspection',
      CHECK_IN: 'Check in',
      CHECK_OUT: 'Check out',
      RECEIVE: 'Receive',
      ORDERED: 'Ordered',
      REPLENISHMENT: 'Replenishment',
      ONBOARDING: 'Onboarding',
    },
  },
  DeliveryPortal: {
    Title: 'Delivery Portal',
    Toast: {
      merging: 'Merging upload',
    },
    Menu: {
      Filter: 'Filter',
      Upload: 'Upload CSV',
      All: 'All Deliveries',
      UploadList: 'Upload List',
    },
    Table: {
      deliveryNumber: 'Delivery Number',
      kitCode: 'KIT Code',
      kitSerial: 'KIT Serial',
      shippingDate: 'Shipping Date',
      inspectionStatus: 'Inspection Status',
      previousOrderCompleted: 'Previous Order Completed',
      overwrite: 'Create New Order',
      noResults: 'No results',
      setExists: 'Set Data Available',
      orderExists: 'Unique Order',
      validGsOneCode: 'GS-1 Code Valid',
    },
    FileUploadListTable: {
      status: 'Status',
      fileName: 'File Name',
      uploader: 'Uploader',
      date: 'Date Uploaded',
      totalDeliveries: 'Total Deliveries',
      download: 'Download',
      noResults: 'No results',
      all: 'All',
      sort: 'Sort',
      filter: 'Filter',
      search: 'Search',
      refresh: 'Refresh',
      noAction: 'No action available',
    },
    FileUpload: {
      title: 'New Upload',
      text: 'Drag and drop your file here, or',
      browse: 'browse',
      note: 'Only supports csv files. Max 1 file.',
      error: {
        fileType: 'File type must be "csv"',
        upload: 'Unable to upload file. Check the format and try again',
        setsMissing: 'Some sets in this file do not exist in SAVI. Please create these sets and try again.',
      },
      success: {
        upload: 'Successfully uploaded',
      },
      scan: 'Scanning for duplicates',
      submit: 'Submit upload',
      cancel: 'Cancel upload',
    },
    DuplicatesModal: {
      heading: 'Duplicates found in uploaded document!',
      subheading:
        'Some of the uploaded inspections already existed in the system. Either upload a new document without duplicates, or review each duplicate.',
      question: 'Do you want to review the duplicates?',
      cancelButton: 'No, go back',
      confirmButton: 'Yes, proceed',
    },
    DuplicatesTable: {
      text: 'Proceeding with uploading this document will ignore all the unselected rows and overwrite the selected rows. Do you wish to continue?',
      confirmButton: 'Yes, proceed',
      cancelButton: 'No, go back',
    },
    ConfirmOverwriteModal: {
      heading: 'Attention!',
      setsNotFound:
        'There are {{notFoundCount}} inspections which cant be merged as the set data does not exist in SADL',
      validGSOneCode:
        'There are {{numInvalidGSOneCodes}} inspections which cant be merged as the provided gs-1 code was invalid',
      subheading:
        'You have selected data to overwrite, continuing will overwrite and remove previously uploaded data. Do you wish to proceed?',
      cancelButton: 'No, go back',
      confirmButton: 'Yes, overwrite and continue',
    },
    MetaData: {
      total: 'Total Inspections',
      notStarted: 'Inspections not started',
      complete: 'Completed Inspections',
    },
    FilterModal: {
      heading: 'Filter',
      status: 'Filter status',
      date: 'Shipping Date Range',
      dateFrom: 'From',
      dateTo: 'To',
      clearFiltersButton: 'Clear Filters',
      applyFiltersButton: 'Apply Filters',
      closeButton: 'Close',
      statusOptions: [
        { value: 'UNCONFIRMED', label: 'Not started' },
        { value: 'CONFIRMED', label: 'Complete' },
      ] as {
        value: KitOrder['status'];
        label: string;
      }[],
    },
  },
  Header: {
    searchPlaceholder: 'Search for a Set or a Device',
    searchNoResults: 'No results found',
    tenantFilter: 'Tenant: ',
  },
};

export default en;
